import { useStore } from "@/stores/useStore";
import {
  CustomerUpdate,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  LOCAL_STORAGE_BASE_KEYS,
} from "@/types/v2";
import { useMemo, useEffect, useRef, useState } from "react";
import { CustomerUpdateListItem } from "./CustomerUpdateListItem";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Link } from "react-router-dom";
import { Tooltip } from "@/components/Common/Tooltip";
import { Button } from "@/components/Base/Button";
import { Badge } from "@/components/Base/Badge";
import { statusClasses } from "@/lib/customerUpdates";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";
import { addMinutes } from "date-fns";
import { localStorageManager } from "@/services/localStorage";
import { Switch } from "@/components/Base/Switch";
import { Label } from "@/components/Base/Label";

const OVERDUE_FILTER_KEY = "overdue" as const;

export const CustomerUpdatesTab = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const allMyCustomerUpdates = useStore((s) => s.allMyCustomerUpdates);
  const allCustomerUpdates = useStore((s) => s.customerUpdates);
  const shopId = useStore((s) => s.shopId);
  const [selectedStatuses, setSelectedStatuses] = useState<
    Set<CustomerUpdateStatus | typeof OVERDUE_FILTER_KEY>
  >(new Set());
  const [expandedUpdateId, setExpandedUpdateId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { customerUpdatesCompletedOnTimeMin } = useCustomerUpdates();
  const [showAllUpdates, setShowAllUpdates] = useState(
    localStorageManager.getItem(
      LOCAL_STORAGE_BASE_KEYS.CUSTOMER_UPDATES_PANEL_SHOW_ALL,
      shopId
    ) === "true"
  );

  // Toggle between showing all updates or only user's updates
  const toggleShowAllUpdates = (value: boolean) => {
    setShowAllUpdates(value);
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.CUSTOMER_UPDATES_PANEL_SHOW_ALL,
      shopId,
      value.toString()
    );
  };

  // Determine which updates to show based on the toggle
  const relevantCustomerUpdates = useMemo(() => {
    return showAllUpdates ? allCustomerUpdates : allMyCustomerUpdates;
  }, [showAllUpdates, allCustomerUpdates, allMyCustomerUpdates]);

  const isUpdateOverdue = (update: CustomerUpdate) => {
    const dueDateTime =
      firestoreToDateTime(update.scheduledUpdateTime) ||
      firestoreToDateTime(update.desiredUpdateTime);

    return (
      dueDateTime &&
      addMinutes(dueDateTime, customerUpdatesCompletedOnTimeMin) < new Date() &&
      update.status === CustomerUpdateStatus.PENDING
    );
  };

  const availableUpdates = useMemo(() => {
    return relevantCustomerUpdates
      .filter((update) =>
        [
          CustomerUpdateStatus.SNOOZED as string,
          CustomerUpdateStatus.CREATED as string,
          CustomerUpdateStatus.SCHEDULED as string,
          CustomerUpdateStatus.PENDING as string,
        ].includes(update.status)
      )
      .sort(
        (a, b) =>
          (
            firestoreToDateTime(a.actualUpdateTime ?? a.scheduledUpdateTime) ??
            new Date(0)
          ).getTime() -
          (
            firestoreToDateTime(b.actualUpdateTime ?? b.scheduledUpdateTime) ??
            new Date(0)
          ).getTime()
      );
  }, [relevantCustomerUpdates]);

  const filteredUpdates = useMemo(() => {
    if (selectedStatuses.size === 0) {
      return availableUpdates;
    }
    return availableUpdates.filter((update) => {
      const updateIsOverdue = isUpdateOverdue(update);

      // Check if overdue filter is selected
      if (selectedStatuses.has(OVERDUE_FILTER_KEY) && updateIsOverdue) {
        return true;
      }

      // Check if status filter is selected
      // If update is overdue and status is PENDING, don't show it in PENDING filter
      if (selectedStatuses.has(update.status as CustomerUpdateStatus)) {
        return !(
          update.status === CustomerUpdateStatus.PENDING && updateIsOverdue
        );
      }

      return false;
    });
  }, [
    availableUpdates,
    selectedStatuses,
    customerUpdatesCompletedOnTimeMin,
    currentTime,
  ]);

  const updateStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      [CustomerUpdateStatus.SNOOZED]: 0,
      [CustomerUpdateStatus.CREATED]: 0,
      [CustomerUpdateStatus.SCHEDULED]: 0,
      [CustomerUpdateStatus.PENDING]: 0,
      [OVERDUE_FILTER_KEY]: 0,
    };
    availableUpdates.forEach((update) => {
      const updateIsOverdue = isUpdateOverdue(update);

      if (counts[update.status] !== undefined) {
        counts[update.status]++;
      }

      // Count overdue updates
      if (updateIsOverdue) {
        counts[OVERDUE_FILTER_KEY]++;
        // Remove overdue updates from the PENDING count
        if (update.status === CustomerUpdateStatus.PENDING) {
          counts[CustomerUpdateStatus.PENDING]--;
        }
      }
    });
    return counts;
  }, [availableUpdates, customerUpdatesCompletedOnTimeMin, currentTime]);

  const toggleStatusFilter = (
    status: CustomerUpdateStatus | typeof OVERDUE_FILTER_KEY
  ) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  // Update current time every minute to recalculate overdue status
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, []);

  // Scroll to top when tab is opened
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, []);

  if (availableUpdates.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-full w-full flex-col gap-3 overflow-auto p-4 text-center"
      >
        <div className={"mb-4 flex w-full flex-row justify-end text-primary"}>
          <div className="flex flex-row items-center justify-between gap-4">
            <Label>Only Mine</Label>
            <Switch
              checked={showAllUpdates}
              onCheckedChange={toggleShowAllUpdates}
            />
            <Label>All</Label>
          </div>
          <div className="flex-grow"></div>
          <Tooltip message={"View All Customer Updates"} className={"z-[100]"}>
            <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
              <Link to={`/${shopId ?? ""}/customer-updates`}>
                <Icon name={IconNames.customerUpdate} />
              </Link>
            </Button>
          </Tooltip>
        </div>
        <div className="flex-grow justify-center text-muted-foreground">
          <Icon
            name={IconNames.bell}
            className="mx-auto mb-4 h-12 w-12 text-muted-foreground"
          />
          <p className="text-lg font-medium">No customer updates</p>
          <p className="mt-2 text-sm">
            {showAllUpdates ? "There are not" : "You don't have"} any snoozed,
            unscheduled, scheduled, or due customer updates at the moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full flex-col gap-3 overflow-auto p-4"
    >
      <div className={"flex flex-row justify-end text-primary"}>
        <div className="flex flex-row items-center justify-between gap-4">
          <Label>Only Mine</Label>
          <Switch
            checked={showAllUpdates}
            onCheckedChange={toggleShowAllUpdates}
          />
          <Label>All</Label>
        </div>
        <div className="flex-grow"></div>
        <Tooltip message={"View All Customer Updates"} className={"z-[100]"}>
          <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
            <Link to={`/${shopId ?? ""}/customer-updates`}>
              <Icon name={IconNames.customerUpdate} />
            </Link>
          </Button>
        </Tooltip>
      </div>
      <div className={"flex flex-row items-center justify-between"}>
        <div className="flex flex-grow flex-row flex-wrap gap-2">
          {/* Overdue badge - always show first if there are overdue updates */}
          {updateStatusCounts[OVERDUE_FILTER_KEY] > 0 && (
            <Badge
              onClick={() => toggleStatusFilter(OVERDUE_FILTER_KEY)}
              className={cn(
                "cursor-pointer bg-destructive text-destructive-foreground transition-all hover:bg-destructive/90",
                selectedStatuses.has(OVERDUE_FILTER_KEY)
                  ? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
                  : selectedStatuses.size === 0
                    ? ""
                    : "opacity-70 hover:opacity-100"
              )}
            >
              <Icon name={IconNames.warning} size={12} className="mr-1" />
              Overdue: {updateStatusCounts[OVERDUE_FILTER_KEY]}
            </Badge>
          )}
          {/* Status badges */}
          {Object.entries(updateStatusCounts)
            .filter(([status]) => status !== OVERDUE_FILTER_KEY)
            .map(
              ([status, count]) =>
                count > 0 && (
                  <Badge
                    key={status}
                    onClick={() =>
                      toggleStatusFilter(status as CustomerUpdateStatus)
                    }
                    className={cn(
                      "cursor-pointer transition-all",
                      statusClasses[status as CustomerUpdateStatus] as string,
                      selectedStatuses.has(status as CustomerUpdateStatus)
                        ? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
                        : selectedStatuses.size === 0
                          ? ""
                          : "opacity-70 hover:opacity-100"
                    )}
                  >
                    {CustomerUpdateStatusLabels[status as CustomerUpdateStatus]}
                    : {count}
                  </Badge>
                )
            )}
          {selectedStatuses.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedStatuses(new Set())}
              className="h-6 px-2 text-xs"
            >
              Clear filters
            </Button>
          )}
        </div>
      </div>
      {filteredUpdates.length === 0 && selectedStatuses.size > 0 ? (
        <div className="flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
          <Icon name={IconNames.filters} className="mx-auto mb-2 h-8 w-8" />
          <p className="text-sm">No updates match the selected filters.</p>
        </div>
      ) : (
        filteredUpdates.map((update) => (
          <CustomerUpdateListItem
            key={update.id}
            update={update}
            isExpanded={expandedUpdateId === update.id}
            onToggleExpand={() =>
              setExpandedUpdateId(
                expandedUpdateId === update.id ? null : update.id
              )
            }
          />
        ))
      )}
    </div>
  );
};
